import {Box, HStack, Show} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {useLocation} from 'react-router-dom';

import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {DevSettings} from '@dms/dev-settings';
import {featureFlags} from '@dms/feature-flags';
import {sourcingRoutes} from '@dms/routes';
import {useHasMultipleBranches, usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {TOP_BAR_ITEM_SIZE} from '../constants/topBarItemSize';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {AuthProfileSelect} from './AuthProfileSelect';
import {AwayStatusSelect} from './AwayStatusSelect';
import {BranchSelect} from './BranchSelect';
import {HeaderContent} from './HeaderContent';
import {HideOnMobileNavigation} from './HideOnMobileNavigation';
import {Notifications} from './Notifications';
import {ReleasePopup} from './ReleasePopup';
import {Settings} from './Settings';
import {TaskCenter} from './TaskCenter';
import {UserAvatar} from './UserAvatar';

interface TopBarProps extends RequiredTestIdProps {
  iconSize: UserSidebarIconSize;
  activeModule?: MenuItemProps;
  settingsModule?: MenuItemProps;
}

export function TopBar(props: TopBarProps) {
  const {colors} = useTheme();
  const location = useLocation();
  const isMobileNavigation = useIsMobileNavigation();

  const [hasReadSettingsPermission] = usePermissions({permissionKeys: ['readSettings']});
  const [canReadTasks] = usePermissions({
    permissionKeys: ['readTask'],
  });

  const {hasMultipleBranches} = useHasMultipleBranches();

  return (
    <>
      <meta name="theme-color" content={colors.palettes.neutral[900][100]} />
      <Box
        zIndex="HEADER"
        paddingLeft={[0, 0, 4]}
        height="100%"
        width="100%"
        overflow="hidden"
        backgroundColor={isMobileNavigation ? 'palettes.neutral.900.100' : 'general.white'}
        borderColor={isMobileNavigation ? undefined : 'general.separator'}
        borderBottom={isMobileNavigation ? undefined : '1px solid'}
      >
        <HStack width="100%" align="center" justify="space-between" height="100%">
          <HeaderContent
            title={props.activeModule?.layoutProps?.title}
            data-testid={suffixTestId('header-content', props)}
          />
          <HStack shrink={0} align="center" justify="flex-end">
            <Show
              whenFeatureEnabled={featureFlags.AUTH_PROFILE_SELECTOR}
              when={!location.pathname.includes(sourcingRoutes.home)}
            >
              <HideOnMobileNavigation>
                <AuthProfileSelect />
              </HideOnMobileNavigation>
            </Show>
            <Show when={hasMultipleBranches}>
              <HideOnMobileNavigation>
                <BranchSelect />
              </HideOnMobileNavigation>
            </Show>
            <HideOnMobileNavigation>
              <AwayStatusSelect />
            </HideOnMobileNavigation>
            <Show whenFeatureEnabled={featureFlags.CORE_TASK_MANAGEMENT} when={canReadTasks}>
              <TaskCenter iconSize={props.iconSize} />
            </Show>
            <Show whenFeatureEnabled={featureFlags.NOTIFICATION_CENTER}>
              <Notifications iconSize={props.iconSize} data-testid="layout-header" />
            </Show>
            <HideOnMobileNavigation>
              <ReleasePopup data-testid="layout-header" iconSize={props.iconSize} />
              <Show when={hasReadSettingsPermission}>
                <Settings
                  settingsModule={props.settingsModule}
                  iconSize={props.iconSize}
                  data-testid="layout-header"
                />
              </Show>
              <Show when={process.env.ENV_TYPE === 'dev'}>
                <DevSettings
                  width={TOP_BAR_ITEM_SIZE}
                  height={TOP_BAR_ITEM_SIZE}
                  iconSize={props.iconSize}
                  data-testid="layout-header"
                />
              </Show>
            </HideOnMobileNavigation>
            <UserAvatar data-testid="layout-header" />
          </HStack>
        </HStack>
      </Box>
    </>
  );
}
